import { useState } from 'react'
import { toast } from 'sonner'
import { MonitorCheck, RefreshCw, Sun, Moon, Check, Laptop } from 'lucide-react'
import { PlanUsageSection } from '@/components/shared/PlanUsageSection'
import { PlanList } from './plans/PlanList'
import { PaymentTypeList } from './payment-types/PaymentTypeList'
import { BulkImport } from './BulkImport'
import { BillingTab } from './BillingTab'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { useTheme } from '@/hooks/useTheme'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'

export function Settings() {
  const { appVersion, isElectron, proxyUrl, proxyToken } = useProxyConfig()
  const { toggle, isDark } = useTheme()
  const [checkingUpdate, setCheckingUpdate] = useState(false)

  async function handleCheckUpdate() {
    if (!proxyUrl || !proxyToken) return
    setCheckingUpdate(true)
    try {
      const res = await fetch(`${proxyUrl}/system/check-updates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}` }
      })
      const result = await res.json()
      if (result.status === 'up-to-date') {
        toast.success('Estás en la última versión ✓')
      } else if (result.status === 'update-available') {
        toast.success(`Nueva versión ${result.version} disponible — descargando...`)
      } else if (result.status === 'not-packaged') {
        toast('Actualizaciones no disponibles en modo desarrollo')
      } else {
        toast.error(result.message || 'Error al verificar actualizaciones')
      }
    } catch {
      toast.error('No se pudo conectar para verificar actualizaciones')
    } finally {
      setCheckingUpdate(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-text-primary font-bold text-xl leading-tight">Configuración</h1>

      <Tabs defaultValue="general">
        <TabsList className="bg-surface border border-border h-auto p-1 rounded-xl gap-0.5">
          <TabsTrigger
            value="general"
            className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all"
          >
            General
          </TabsTrigger>
          <TabsTrigger
            value="planes"
            className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all"
          >
            Planes
          </TabsTrigger>
          <TabsTrigger
            value="pagos"
            className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all"
          >
            Productos y servicios
          </TabsTrigger>
          <TabsTrigger
            value="importar"
            className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all"
          >
            Importar datos
          </TabsTrigger>
          <TabsTrigger
            value="facturacion"
            className="rounded-lg px-4 py-2 text-xs font-semibold text-text-muted data-[state=active]:bg-background data-[state=active]:text-text-primary data-[state=active]:shadow-sm transition-all"
          >
            Facturación
          </TabsTrigger>
        </TabsList>

        {/* ── General ── */}
        <TabsContent value="general" className="mt-5 flex flex-col gap-6">

          {/* Uso del plan */}
          <PlanUsageSection />

          {/* Apariencia */}
          <section className="flex flex-col gap-3">
            <h2 className="text-text-muted text-xs font-semibold uppercase tracking-widest">Apariencia</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">

              {/* Claro */}
              <button
                onClick={() => isDark && toggle()}
                className={`flex flex-col items-start gap-3 p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                  !isDark
                    ? 'border-accent/40 bg-accent/5 shadow-sm'
                    : 'border-border bg-surface hover:border-accent/20 hover:bg-surface'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${!isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-background border border-border'}`}>
                  <Sun className={`w-4 h-4 ${!isDark ? 'text-amber-500' : 'text-text-muted'}`} />
                </div>
                <div className="flex-1 w-full">
                  <div className="flex items-center justify-between">
                    <p className="text-text-primary text-sm font-semibold">Modo claro</p>
                    {!isDark && <Check className="w-3.5 h-3.5 text-accent shrink-0" />}
                  </div>
                  <p className="text-text-muted text-xs mt-0.5">Fondo blanco, ideal para ambientes iluminados</p>
                </div>
              </button>

              {/* Oscuro */}
              <button
                onClick={() => !isDark && toggle()}
                className={`flex flex-col items-start gap-3 p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                  isDark
                    ? 'border-accent/40 bg-accent/5 shadow-sm'
                    : 'border-border bg-surface hover:border-accent/20 hover:bg-surface'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-background border border-border'}`}>
                  <Moon className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-text-muted'}`} />
                </div>
                <div className="flex-1 w-full">
                  <div className="flex items-center justify-between">
                    <p className="text-text-primary text-sm font-semibold">Modo oscuro</p>
                    {isDark && <Check className="w-3.5 h-3.5 text-accent shrink-0" />}
                  </div>
                  <p className="text-text-muted text-xs mt-0.5">Fondo oscuro, reduce el cansancio visual</p>
                </div>
              </button>

            </div>
          </section>

          {/* Aplicación (solo Electron) */}
          {isElectron && (
            <section className="flex flex-col gap-3">
              <h2 className="text-text-muted text-xs font-semibold uppercase tracking-widest">Aplicación de escritorio</h2>
              <div className="bg-surface border border-border rounded-2xl p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                  <Laptop className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary text-sm font-bold leading-tight">Veridia</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                      <MonitorCheck className="w-3 h-3" /> Conectado
                    </span>
                    {appVersion && (
                      <span className="text-[10px] font-mono text-text-muted bg-background border border-border px-2 py-0.5 rounded-full">
                        v{appVersion}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleCheckUpdate}
                  disabled={checkingUpdate}
                  className="flex items-center gap-2 px-4 py-2.5 bg-background border border-border hover:bg-accent hover:border-accent hover:text-background text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${checkingUpdate ? 'animate-spin' : ''}`} />
                  {checkingUpdate ? 'Buscando...' : 'Actualizar'}
                </button>
              </div>
            </section>
          )}

        </TabsContent>

        {/* ── Planes (membresías del gimnasio) ── */}
        <TabsContent value="planes" className="mt-5">
          <PlanList />
        </TabsContent>

        {/* ── Métodos de pago ── */}
        <TabsContent value="pagos" className="mt-5">
          <PaymentTypeList />
        </TabsContent>

        {/* ── Importar datos ── */}
        <TabsContent value="importar" className="mt-5">
          <BulkImport />
        </TabsContent>

        {/* ── Facturación ── */}
        <TabsContent value="facturacion" className="mt-5">
          <BillingTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
